import * as THREE from "three";
import {Node, PropertyOrKeyValue} from "../../Node";
import CircleUvBufferGeometry from "oc-threejs/geometries/CircleUvBufferGeometry";
import Maths3 from "oc-threejs/Maths3";
import OcMaterialHelper from "oc-threejs/objects/OcMaterialHelper";
import {PNodeRef} from "../../properties/PNodeRef";
import {PNumber} from "../../properties/PNumber";
import {PColor} from "../../properties/PColor";
import {PBoolean} from "../../properties/PBoolean";
import Scenes from "oc-modules/oc-threejs/Scenes";
import Commons from "oc-modules/oc-commons/Commons";
import {EnvironmentJson} from "../EnvironmentJson";
import {v2} from "oc-threejs/short3";
import {PEuler} from "../../properties/PEuler";
import Doms from "oc-commons/Doms";
import {ScreenUtils} from "oc-threejs/ScreenUtils";
import {PString} from "../../properties/PString";

export class EnvironmentNode extends Node {

    parent: EnvironmentJson;
    SkyMap: string;
    OtherEnvMap: boolean;
    EnvMap: string;
    SkyY: number;
    GroundMaterial: string;
    FogColor: string;
    FogPower: number;
    GroundY: number;
    GroundRasterMaterial: string;
    GroundRasterY: number;
    GroundRasterStep: number
    GroundRasterRadius: number;
    GroundRasterSegments: number;
    GroundRasterType: string;
    SceneModel: string;
    Farbkalibrierung: boolean;
    Spiegelkugel: boolean;
    SkyDistance: number;
    SkyRotation: number[];
    sky3d: THREE.Object3D;
    ground3d: THREE.Object3D;
    sceneModel3d: THREE.Object3D;
    groundRaster3d: THREE.Group;
    farbkalibrierung3d: THREE.Object3D;
    farbkalibrierungplanes3d: THREE.Object3D;
    farbkalibrierungplanesDom: Element;
    spiegelkugel3d: THREE.Object3D;

    constructor(parent: EnvironmentJson) {
        super();
        this.parent = parent;
    }

    createProperties() {
        return [
            new PNodeRef({
                "name": "SkyMap",
                "nodeList": (property) => APP.sceneNode.findProperty("EnvMapJson"),
                "comment": I18N("scene editor:environment skymap"),
                node: this
            }),
            new PNumber({
                "name": "SkyDistance",
                "default": 1000,
                "min": 0,
                "max": 10000,
                "decimals": 0,
                "comment": I18N("scene editor:environment sky distance"),
                "showInEditor": (p) => p.node.SkyMap != null,
                node: this
            }),
            new PNumber({
                "name": "SkyY",
                "default": 0.0,
                "min": -100,
                "max": 100,
                "decimals": 2,
                "comment": I18N("scene editor:environment sky Y"),
                "showInEditor": (p) => p.node.SkyMap != null,
                node: this
            }),
            new PEuler({
                "name": "SkyRotation",
                "comment": I18N("scene editor:environment sky rotation"),
                "showInEditor": (p) => p.node.SkyMap != null,
                node: this
            }),
            {separator: true},
            new PBoolean({
                "name": "OtherEnvMap",
                "default": false,
                "comment": I18N("scene editor:environment other envmap"),
                node: this
            }),
            new PNodeRef({
                "name": "EnvMap",
                "nodeList": (property) => APP.sceneNode.findProperty("EnvMapJson"),
                "comment": I18N("scene editor:environment envmap"),
                "showInEditor": (p) => p.node.OtherEnvMap,
                node: this
            }),
            {separator: true},
            new PNodeRef({
                "name": "GroundMaterial",
                "nodeList": (property) => APP.sceneNode.findProperty("MaterialJson"),
                "comment": I18N("scene editor:environment ground material"),
                node: this
            }),
            new PNumber({
                "name": "GroundY",
                "default": -0.01,
                "min": -2,
                "max": 2,
                "decimals": 2,
                "comment": I18N("scene editor:environment ground Y"),
                "showInEditor": (p) => p.node.GroundMaterial != null,
                node: this
            }),
            {separator: true},
            new PNodeRef({
                "name": "GroundRasterMaterial",
                "nodeList": (property) => APP.sceneNode.findProperty("MaterialJson"),
                "comment": I18N("scene editor:environment ground raster material"),
                node: this
            }),
            new PNumber({
                "name": "GroundRasterRadius",
                "default": 100,
                "min": 0,
                "max": 10000,
                "decimals": 2,
                "comment": I18N("scene editor:environment ground raster radius"),
                "showInEditor": (p) => p.node.GroundRasterMaterial != null,
                node: this
            }),
            new PNumber({
                "name": "GroundRasterStep",
                "default": 1,
                "min": 0.1,
                "max": 1000,
                "decimals": 2,
                "comment": I18N("scene editor:environment ground raster step"),
                "showInEditor": (p) => p.node.GroundRasterMaterial != null,
                node: this
            }),
            new PNumber({
                "name": "GroundRasterSegments",
                "default": 16,
                "min": 1,
                "max": 360,
                "decimals": 0,
                "comment": "Anzahl der Winkel-Unterteilungen des Rasters",
                "showInEditor": (p) => p.node.GroundRasterMaterial != null && p.node.GroundRasterType=='polar',
                node: this
            }),
            new PNumber({
                "name": "GroundRasterY",
                "default": 0.03,
                "min": -2,
                "max": 2,
                "decimals": 2,
                "comment": I18N("scene editor:environment ground raster Y"),
                "showInEditor": (p) => p.node.GroundRasterMaterial != null,
                node: this
            }),
            new PString({
                "name": "GroundRasterType",
                "default": "grid",
                "selectOptions": ["grid", "polar"],
                "comment": "Soll das Raster rechteckig (grid) oder kreisförmig (polar) sein?",
                "showInEditor": (p) => p.node.GroundRasterMaterial != null,
                node: this
            }),
            {separator: true},
            new PNodeRef({
                "name": "SceneModel",
                "nodeList": (property) => APP.sceneNode.findProperty("Model"),
                "comment": I18N("scene editor:environment scene model"),
                node: this
            }),
            {separator: true},
            new PColor({
                "name": "FogColor",
                "comment": I18N("scene editor:environment fog color"),
                node: this
            }),
            new PNumber({
                "name": "FogPower",
                "comment": I18N("scene editor:environment fog power"),
                "default": 0.00025,
                "step": 0.00005,
                "min": 0,
                "max": 0.01,
                "decimals": 5,
                "showInEditor": (p) => p.node.FogColor != null,
                node: this
            }),
            {separator: true},
            new PBoolean({
                "name": "Farbkalibrierung",
                "label": I18N("scene editor:color calibration"),
                "default": false,
                "comment": I18N("scene editor:environment color calibration helper"),
                node: this
            }),
            new PBoolean({
                "name": "Spiegelkugel",
                "label": I18N("scene editor:mirror sphere"),
                "default": false,
                "comment": I18N("scene editor:environment mirror sphere"),
                node: this
            }),
        ] as PropertyOrKeyValue[];
    }

    init() {
        super.init();
        this.updateScene();
        return this;
    }

    updateScene() {
        this._dataChanged = {};
        this._updateSky();
        this._updateEnvMap();
        this._updateGround();
        this._updateGroundRaster();
        this._updateSceneModel();
        this._updateFog();
        this._updateHelper();
    }

    getTextureVersion(envMap) {
        const envMapNode = APP.sceneNode.getEnvMapNode(envMap);
        return envMapNode?.texture3d?.image ? envMapNode?.imageVersions : -1;
    }

    get SkyMapTextureId() {
        return this.getTextureVersion(this.SkyMap);
    }

    get EnvMapTextureId() {
        return this.getTextureVersion(this.EnvMap);
    }

    _updateSky() {
        if (!(this.changed('SkyMap')
            || this.changed('SkyMapTextureId')
            || this.changed('SkyDistance')
            || this.changed('SkyRotation')
            || this.changed('SkyY'))) {
            return;
        }
        Scenes.remove(this.sky3d);
        this.sky3d = null;
        const texture = APP.sceneNode.getEnvMapNode(this.SkyMap);
        if (texture?.texture3d?.image) {
            const skyMaterial = new THREE.MeshBasicMaterial({
                map: texture.texture3d,
                side: THREE.FrontSide,
                fog: false
            });
            const radius = this.getSkyDistance();
            const geometry = texture.isEuirectangular
                ? new THREE.SphereBufferGeometry(radius, 48, 24)
                : new THREE.BoxBufferGeometry(radius * 2, radius * 2, radius * 2);
            // ohne die Scale z=-1 wäre die EnvMap vs. SkyMap invertiert
            geometry.scale(1, 1, -1);
            const sky = new THREE.Mesh(geometry, skyMaterial);
            sky.name = 'sky';
            sky.raycastVisibleTags = [];
            sky.frustumCulled = false;
            sky.position.setY(this.SkyY);
            sky.rotation.fromArray(this.SkyRotation ?? [0,0,0]);
            sky.isSceneBackground = true;
            APP.scene.add(sky);
            this.sky3d = sky;
        }
        APP.sceneNode.triggers.add('SceneBackgroundChange');
    }

    _updateEnvMap() {
        if (!(this.changed('EnvMap')
            || this.changed('EnvMapTextureId')
            || this.changed('OtherEnvMap')
            || this.changed('SkyMap')
            || this.changed('SkyMapTextureId'))) {
            return;
        }
        const map = this.OtherEnvMap ? this.EnvMap : this.SkyMap;
        const texture3d = APP.sceneNode.getEnvMapNode(map)?.texture3d;
        STAGE.scene.environment = texture3d;
    }

    _updateGround() {
        if (!(this.changed('GroundMaterial') || this.changed('SkyDistance') || this.changed('GroundY') || this.changed('Shadow'))) {
            return;
        }
        Scenes.remove(this.ground3d);
        this.ground3d = null;
        if (!Commons.isEmpty(this.GroundMaterial)) {
            const material = APP.sceneNode.getMaterial(this.GroundMaterial);
            const radius = this.getSkyDistance();
            const geometry = new CircleUvBufferGeometry(radius, 48);
            const ground = new THREE.Mesh(geometry, material);
            ground.name = 'ground';
            ground.raycastVisibleTags = [];
            ground.quaternion.setFromUnitVectors(Maths3.vector.z, Maths3.vector.y);
            ground.position.setY(this.GroundY);
            ground.receiveShadow = this.parent.rendererNode.ShadowsEnabled;
            ground.isSceneBackground = true;
            APP.scene.add(ground);
            this.ground3d = ground;
            APP.sceneNode.triggers.add('SceneBackgroundChange');
        }
    }

    _updateGroundRaster() {
        if (!(this.changed('GroundRasterMaterial')
            || this.changed('GroundRasterY')
            || this.changed('GroundRasterStep')
            || this.changed('GroundRasterSegments')
            || this.changed('GroundRasterRadius')
            || this.changed('GroundRasterType')
        )) {
            return;
        }
        Scenes.remove(this.groundRaster3d);
        this.groundRaster3d = null;
        const onlyPositionGeometry = (line) => {
            const geometry = new THREE.BufferGeometry();
            const position = line.geometry.getAttribute('position').clone();
            geometry.setAttribute('position', position);
            return geometry;
        }
        const gridGeometry = () => {
            return onlyPositionGeometry(new THREE.GridHelper(this.GroundRasterRadius, this.GroundRasterRadius / this.GroundRasterStep));
        }
        const polarGeometry = () => {
            return onlyPositionGeometry(new THREE.PolarGridHelper(this.GroundRasterRadius, this.GroundRasterSegments, this.GroundRasterRadius / this.GroundRasterStep));
        }
        const material = this.GroundRasterMaterial && APP.sceneNode.getMaterial(this.GroundRasterMaterial);
        if (material) {
            const geometry = this.GroundRasterType == 'grid' ? gridGeometry() : polarGeometry();
            this.groundRaster3d = new THREE.LineSegments(geometry, material);
            this.groundRaster3d.name = 'groundRaster';
            this.groundRaster3d.isSceneBackground = true;
            this.groundRaster3d.position.y = this.GroundRasterY;
            // this.groundRaster3d.renderOrder = 3;
            APP.scene.add(this.groundRaster3d);
        }
    }

    _updateSceneModel() {
        if (!(this.changed('SceneModel'))) {
            return;
        }
        APP.sceneNode.loadModelPromise(this.SceneModel).then(model => {
            Scenes.remove(this.sceneModel3d);
            this.sceneModel3d = model;
            if (model) {
                APP.scene.add(model);
                model.isSceneBackground = true;
            }
            APP.sceneNode.triggers.add('SceneBackgroundChange');
        });
    }

    _updateFog() {
        if (!(this.changed('FogColor') || this.changed('FogPower'))) {
            return;
        }
        if (this.FogColor) {
            APP.scene.fog = new THREE.FogExp2(new THREE.Color(this.FogColor), this.FogPower);
        } else {
            APP.scene.fog = null;
        }
    }

    _updateHelper() {
        if (this.changed('Farbkalibrierung')) {
            Scenes.remove(this.farbkalibrierung3d);
            this.farbkalibrierung3d = null;

            Scenes.remove(this.farbkalibrierungplanes3d);
            this.farbkalibrierungplanes3d = null;

            this.farbkalibrierungplanesDom?.parentNode?.removeChild(this.farbkalibrierungplanesDom);
            this.farbkalibrierungplanesDom = null;

            if (this.Farbkalibrierung) {
                this.createFarbkalibrierung3d();
                // muss so für oc-messestand
                Commons.sleepUntil(() => document.querySelector('.scene-editor-app-wrapper'))
                    .then(() => this.createFarbkalibrierungsPlanes3d());
            }
        }
        if (this.changed('Spiegelkugel')) {
            Scenes.remove(this.spiegelkugel3d);
            this.spiegelkugel3d = null;
            if (this.Spiegelkugel) {
                this.createSpiegelkugel3d();
            }
        }
    }

    private createFarbkalibrierung3d() {
        this.farbkalibrierung3d = new OcMaterialHelper(2);
        this.farbkalibrierung3d.name = "Farbkalibrierung";
        APP.scene.add(this.farbkalibrierung3d);
    }

    private createFarbkalibrierungsPlanes3d() {
        this.farbkalibrierungplanes3d = new THREE.Group();
        this.farbkalibrierungplanes3d.name = 'FarbkalibrierungPlanes3d';
        APP.camera.add(this.farbkalibrierungplanes3d);

        // erzeugt werden Quadrate mit jeweils einer Farbe
        // der rechte Teil des Quadrats ist ein DOM-Element, das die Farbe
        // unverfälscht von Three anzeigt
        // der linke Teil besteht aus zwei Quadraten, die innerhalb der Szene sind,
        // aber direkt vor der Kamera schweben. Beide Quadrate sind von den Render
        // Settings abhängig. Das obere Quadrat ist ein MeshStandardMaterial und
        // reagiert auf Licht, das untere Quadrat ist ein MeshBasicMaterial und
        // reagiert nicht auf Licht

        const createColorLeftSide3d = (colorStr, domRect) => {
            const screenUtils = new ScreenUtils();
            const cameraDistance = 1;
            const [topleft, bottomright] =
                [v2(domRect.left, domRect.top), v2(domRect.right + 2, domRect.bottom)]
                    .map(v => screenUtils.screenToCameraCoordinates(Maths3.v2To3(v, cameraDistance)));
            const boxOnScreen = new THREE.Box3().setFromPoints([topleft, bottomright]);
            const size = boxOnScreen.getSize();
            size.y *= 0.5;
            const color = STAGE.convertSRGBToLinear(new THREE.Color(colorStr));
            // const geometry = new THREE.PlaneGeometry(size.x, size.y);
            const geometry = new THREE.BoxGeometry(size.x, size.x * 0.5, size.y);
            const createColorPlane = (material, position) => {
                const plane = new THREE.Mesh(geometry, material);
                plane.position.copy(position);
                plane.rotation.x = 90 * Math.PI / 180;
                plane.name = colorStr;
                return plane;
            }
            [
                createColorPlane(
                    new THREE.MeshStandardMaterial({color}),
                    boxOnScreen.grid(0.5, 0.25, 0)),
                createColorPlane(
                    new THREE.MeshBasicMaterial({color, side: THREE.DoubleSide}),
                    boxOnScreen.grid(0.5, 0.75, 0))
            ].forEach(p => this.farbkalibrierungplanes3d?.add(p));
        }

        const createDiv = (className) => {
            const div = document.createElement('div');
            div.setAttribute('class', className);
            return div;
        }

        this.farbkalibrierungplanesDom = createDiv('farbkalibrierungplanes');
        const n = 7;
        const colorCallbacks = [];

        _.range(n).forEach(i => {
            const colorStr = `hsl(0,0%,${(100 * i / (n - 1)).toFixed(0)}%)`;
            const quadratDiv = createDiv('quadrat');
            const leftDiv = createDiv('left-half');
            quadratDiv.appendChild(leftDiv);
            const rightDiv = createDiv('right-half');
            rightDiv.setAttribute('style', `background-color: ${colorStr}`);
            quadratDiv.appendChild(rightDiv);
            this.farbkalibrierungplanesDom.appendChild(quadratDiv);
            colorCallbacks.push(() =>
                createColorLeftSide3d(colorStr, leftDiv.getBoundingClientRect()));
        });

        const createColorPlanes3d = () => colorCallbacks.forEach(c => c());
        const sceneEditorWrapper = document.querySelector('.scene-editor-app-wrapper');
        sceneEditorWrapper.appendChild(this.farbkalibrierungplanesDom);

        Commons.sleepUntil(
            () => Doms.isElementAttachedToDocument(this.farbkalibrierungplanesDom))
            .then(createColorPlanes3d);

        STAGE.on('setRenderSize', () => {
            Scenes.empty(this.farbkalibrierungplanes3d);
            createColorPlanes3d();
        });

    }

    createSpiegelkugel3d() {
        this.spiegelkugel3d = new THREE.Group();
        this.spiegelkugel3d.name = "Spiegelkugel";
        APP.scene.add(this.spiegelkugel3d);

        const sphere = new THREE.Mesh(
            new THREE.SphereBufferGeometry(6, 30, 30),
            new THREE.MeshStandardMaterial({roughness: 0, metalness: 1, color: 0xffffff}));
        sphere.castShadow = true;
        sphere.receiveShadow = true;
        sphere.position.y += 8;
        this.spiegelkugel3d.add(sphere);

        for (let i = 0; i < 10; i++) {
            const sockel = new THREE.Mesh(
                new THREE.BoxBufferGeometry(30 - i * 2, 30 - i * 2, 0.1),
                new THREE.MeshStandardMaterial({
                    roughness: 1,
                    metalness: 0,
                    color: 0xffffff
                }));
            sockel.rotation.x = Math.PI / 2;
            sockel.castShadow = true;
            sockel.receiveShadow = true;
            sockel.position.y += i * 0.1;
            this.spiegelkugel3d.add(sockel);
        }
    }

    getSkyDistance() {
        let distance = this.SkyDistance;
        if (distance > APP.camera.far * 0.95) {
            console.warn('SkyDistance adjusted because it was far away than camera.far');
            distance = APP.camera.far * 0.9;
        }
        return distance;
    }

    getName() {
        return "Environment";
    }

}
